import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  FIELD_RESTRICTIONS,
  FIELD_RESTRICTION_OBJECTS,
  LOCKED_CELL_REASON,
  PERMISSION_OBJECT_COPY,
  answersOfCell,
  fieldRestrictionLabel,
  isLockedCell,
  isPermissionObjectShown,
  type ModuleState,
  type PermissionObject,
} from '@nest-recruit/shared';
import { Checkbox } from '../../../../components/Checkbox';
import { Chip } from '../../../../components/Chip';
import { Popover, usePopover } from '../../../../components/Popover';
import { Segmented } from '../../../../components/Segmented';
import type { RoleCell } from '../../api/roles-api';
import {
  ACCESS_OPTIONS,
  NONE_MEANS,
  draftCellOf,
  isRowChanged,
  reachOptions,
  withAccess,
  withReach,
  type AreaRows,
  type EditableRole,
  type RoleDraft,
} from './role-editor-model';
import styles from './RoleRows.module.css';

// Roles A: the editor's rows, grouped by area with plain names. Each row asks two questions:
// "Which ones can they open?" and "What can they do?".

interface RoleRowsProps {
  readonly role: EditableRole;
  readonly draft: RoleDraft;
  readonly areas: readonly AreaRows[];
  readonly modules: ModuleState;
  readonly canEdit: boolean;
  readonly onCellChange: (cell: RoleCell) => void;
}

export function RoleRows({ role, draft, areas, modules, canEdit, onCellChange }: RoleRowsProps): ReactNode {
  const rowCount = areas.reduce((total, area) => total + area.objects.length, 0);
  return (
    <div className={styles.frame}>
      <div className={styles.rows}>
        <div className={styles.head} aria-hidden>
          <span>
            What <span className={styles.count}>{rowCount}</span>
          </span>
          <span>Which ones can they open?</span>
          <span>What can they do?</span>
        </div>
        <p className={styles.noneLine}>{NONE_MEANS}</p>

        {areas.map((area) => (
          <section key={area.key} className={styles.area} aria-label={area.label}>
            <h3 className={styles.areaTitle}>{area.label}</h3>
            {area.objects.map((object) => (
              <RoleRow
                key={object}
                role={role}
                object={object}
                cell={draftCellOf(draft, object)}
                isPartOff={!isPermissionObjectShown(object, modules)}
                canEdit={canEdit}
                onChange={onCellChange}
              />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

interface RoleRowProps {
  readonly role: EditableRole;
  readonly object: PermissionObject;
  readonly cell: RoleCell;
  readonly isPartOff: boolean;
  readonly canEdit: boolean;
  readonly onChange: (cell: RoleCell) => void;
}

function RoleRow({ role, object, cell, isPartOff, canEdit, onChange }: RoleRowProps): ReactNode {
  const copy = PERMISSION_OBJECT_COPY[object];
  const isLocked = isLockedCell(role.key, object);
  const answers = answersOfCell(cell, object);
  const isChanged = isRowChanged(role, object, cell);
  const isFixed = !canEdit || isLocked;

  return (
    <div className={styles.row} data-changed={isChanged || undefined}>
      <div className={styles.what}>
        <span className={styles.name}>
          {copy.label}
          {isLocked && <Lock size={12} aria-label="Fixed" />}
          {isPartOff && (
            <Chip tone="neutral" plain>
              Part switched off
            </Chip>
          )}
        </span>
        <span className={styles.description}>{copy.description}</span>
        {isLocked && <span className={styles.lockReason}>{LOCKED_CELL_REASON}</span>}
        <HiddenFields object={object} cell={cell} canEdit={canEdit && answers.reach !== 'none'} onChange={onChange} />
      </div>

      <div className={styles.question}>
        <span className={styles.questionLabel} aria-hidden>
          Which ones can they open?
        </span>
        <Segmented
          label={`${copy.label}: which ones can they open?`}
          value={answers.reach}
          options={reachOptions(role, object)}
          disabled={isFixed}
          onChange={(reach) => onChange(withReach(role, cell, object, reach))}
        />
      </div>

      <div className={styles.question}>
        <span className={styles.questionLabel} aria-hidden>
          What can they do?
        </span>
        <Segmented
          label={`${copy.label}: what can they do?`}
          value={answers.access}
          options={ACCESS_OPTIONS}
          disabled={isFixed || answers.reach === 'none'}
          onChange={(access) => onChange(withAccess(role, cell, object, access))}
        />
      </div>
    </div>
  );
}

const POPOVER_WIDTH = 320;

/**
 * "Shows — for…": the private details this role sees as a dash on this row (§L23 field rules,
 * F2.09). A pill when some are hidden, which opens the list beside it.
 */
function HiddenFields({
  object,
  cell,
  canEdit,
  onChange,
}: {
  readonly object: PermissionObject;
  readonly cell: RoleCell;
  readonly canEdit: boolean;
  readonly onChange: (cell: RoleCell) => void;
}): ReactNode {
  const { anchor, triggerRef, isOpen, toggle, close } = usePopover();
  const catalogue = FIELD_RESTRICTIONS.filter((restriction) => FIELD_RESTRICTION_OBJECTS[restriction.field] === object);
  // Bug list #24: a field saved on the role but no longer in the catalogue still shows, as words.
  const options = [
    ...catalogue.map((restriction) => ({ field: restriction.field, label: restriction.label, note: restriction.note })),
    ...cell.restrictedFields
      .filter((field) => !catalogue.some((restriction) => restriction.field === field))
      .map((field) => ({ field, label: fieldRestrictionLabel(field), note: undefined })),
  ];
  if (options.length === 0) return null;
  const hidden = cell.restrictedFields;
  if (hidden.length === 0 && !canEdit) return null;

  const toggleField = (field: string, isHidden: boolean): void => {
    onChange({ ...cell, restrictedFields: isHidden ? [...hidden, field] : hidden.filter((other) => other !== field) });
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={hidden.length > 0 ? styles.pill : styles.pillQuiet}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={toggle}
      >
        {hidden.length > 0 ? `Shows — for: ${hidden.map(fieldRestrictionLabel).join(', ')}` : 'Hide private details'}
      </button>
      <Popover anchor={anchor} onClose={close} width={POPOVER_WIDTH} label={`Private details on ${PERMISSION_OBJECT_COPY[object].label}`}>
        <div className={styles.fields}>
          <h4 className={styles.fieldsTitle}>Show — instead of</h4>
          <p className={styles.fieldsHint}>This role sees a dash where these would be, on every screen, list and export.</p>
          {options.map((option) => (
            <div key={option.field} className={styles.field}>
              <Checkbox
                label={option.label}
                checked={hidden.includes(option.field)}
                disabled={!canEdit}
                onChange={(checked) => toggleField(option.field, checked)}
              />
              {option.note !== undefined && <span className={styles.fieldNote}>{option.note}</span>}
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
}
